import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import TextField from '../../../components/ui/TextField';
import Checkbox from '../../../components/ui/Checkbox';
import WizardShell from '../../../components/ui/WizardShell';
import BottomBar from '../../../components/ui/BottomBar';
import BookSidePanel from '../../../components/ui/BookSidePanel';
import { Image as ImageIcon } from 'lucide-react';
import { BASE_BOOK_PRICE } from '../../../config/pricing';
import { isEtsyUser } from '../../../services/etsyService';
import useForm from '../../../hooks/useForm';
import useMutation from '../../../hooks/useMutation';
import { bookAdd, bookEdit } from '../../../services/bookService';
import { giftIdFromSearch } from '../../../services/giftService';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { saveStep } from '../../../services/stepService';
import { setActiveBookId } from '../../../redux/slice/stepSlice';
import { toast } from 'react-toastify';
import { addBook, updateBook } from '../../../redux/slice/profileSlice';
import { clearSketch } from '../../../redux/slice/sketchSlice';
import LoadingFallback from '../../../components/elements/LoadingFallback';
import { markStepAsVisited } from '../../../redux/slice/profileSlice';
import { sanitizeBookTag } from '../../../util/bookTag';
import { FUNNEL_EVENTS, pushFunnelEvent } from '../../../util/funnelEvents';
import { BUILDER_STEPS, useBuilderStepVisit } from '../../../util/builderStepTiming';
import { setTitle } from '../../../helpers/MetaTag';
import { isSmartCropEnabled } from '../../../util/imageCropUploadUtils';
import { warmFaceDetector } from '../../../util/faceDetector';
import { notifySaved } from '../../../util/saveTracker';
import CoverPreview from '../../../components/builder/CoverPreview';
import { loadCoverFont, titleFitsCover } from '../../../util/coverPage';
import { titleCanPrint, titleProblem } from '../../../util/printableText';

// Validation rules
const validation = {
  bookName: { required: true, message: 'Book Name is required' },
};

const BookCreation = () => {
  const [searchParams] = useSearchParams();
  const tag = searchParams.get("tag");
  const giftOrderId = giftIdFromSearch(`?${searchParams.toString()}`);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { session } = useSelector((state) => state.authProvider);
  const { booksLoading, myBooks, data: profileData } = useSelector((state) => state.profile);
  const { activeBookId } = useSelector((state) => state.stepFlow);
  useBuilderStepVisit(BUILDER_STEPS.TITLE, activeBookId);

  const activeBook = myBooks?.find((book) => book._id === activeBookId);

  // TODO(design): the "fits on the cover" message isn't drawn (docs/design/GAPS.md).
  const [titleFull, setTitleFull] = useState(false);
  useEffect(loadCoverFont, []);
  const { values, handleChange, handleSubmit, errors } = useForm({
    validationRules: validation,
    initialState: {
      bookName: activeBook?.title ?? '',
    },
  });
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Start fetching the face detector (~4MB, cached after the first visit)
  // while the customer types a title, so the cover and photo steps have it.
  useEffect(() => {
    isSmartCropEnabled().then((on) => { if (on) warmFaceDetector(); });
  }, []);

  useEffect(() => {
    if (!!activeBookId) setAgreeToTerms(true);
  }, [activeBookId]);

  // useMutation for bookAdd
  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      if (!!activeBookId) {
        return await bookEdit({
          id: activeBookId,
          title: val.bookName,
        });
      } else {
        return await bookAdd({
          title: val.bookName,
          description: val.bookDescription,
          // Stamp the purchase format on the book so it survives the redirects,
          // refreshes and re-logins between here and checkout. Create-only: the
          // format belongs to this new book, never to one already in progress.
          tag: sanitizeBookTag(tag),
          // Started from the gift page: the API checks the gift is this
          // recipient's and gives the book a share of the paid copies.
          ...(giftOrderId ? { giftOrderId } : {}),
        });
      }
    },
    onSuccess: async (res) => {
      if (![200, 201].includes(res?.status)) {
        console.error('Book creation failed:', res);
        // A gift book can be refused for a reason worth reading ("you've
        // already started 3 books"); the API words it for the customer.
        toast.error(giftOrderId && res?.message ? res.message : 'Failed to create book');
        return;
      }

      if (!res?.data?._id && !activeBookId) {
        toast.error(
          'Could not save book, something went wrong, please try again.'
        );
        return;
      }

      // Save book ID and add to Redux
      if (!activeBookId) {
        dispatch(clearSketch());
        dispatch(setActiveBookId({ bookId: res.data._id }));
        dispatch(addBook(res.data));
        pushFunnelEvent(FUNNEL_EVENTS.START_BOOK, {
          bookId: res.data._id,
          profile: profileData,
          tag,
        });
      } else {
        dispatch(updateBook({ _id: activeBookId, title: res.data.title }));
      }
      notifySaved();

      // Save step progress in the background (fire-and-forget)
      saveStep({
        userId: session?.user?._id,
        bookId: activeBookId || res.data._id,
        isCompleted: 'yes',
        stepCode: 'Upload_Your_Cover_Photo',
      }).catch(err => console.error('Failed to save step in background:', err));

      dispatch(
        markStepAsVisited({
          id: activeBookId,
          currentStepCode: 'Upload_Your_Cover_Photo',
        })
      );

      // Navigate immediately
      navigate(
        `/dashboard?stepCode=Upload_Your_Cover_Photo${tag ? `&tag=${tag}` : ""}`
      );
    },
    onError: (err) => {
      console.error('Book creation failed:', err);
      toast.error(err?.message || 'Failed to create book');
    },
  });

  setTitle('Memory Doodle | Enter Title')

  if (booksLoading) {
    return <LoadingFallback />;
  }

  // ---- step 1 on the redesign (docs/design/HANDOFF.md: Main, Tab1Title, Desk1Title)
  const title = values?.bookName ?? activeBook?.title ?? '';
  const canContinue = agreeToTerms && titleCanPrint(title) && !isLoading;
  const showPrice = !isEtsyUser(profileData) && !giftOrderId && !activeBook?.gift?.giftOrderId;
  const ideas = ['Our Summer', 'Happy Birthday!', "Grandma's Favorites", 'Family Trip'];
  const pickIdea = (idea) => handleChange({ target: { name: 'bookName', value: idea } });
  const submit = () => {
    if (!canContinue) return;
    handleSubmit({ preventDefault: () => {} }, mutate);
  };

  const terms = (
    <Checkbox checked={agreeToTerms} onChange={() => setAgreeToTerms(!agreeToTerms)}>
      I agree to the{' '}
      <a className='font-semibold text-md-pink-dark underline' href={`${process.env.REACT_APP_SHOPIFY_URL}/policies/terms-of-service`} target='_blank' rel='noreferrer'>
        Terms
      </a>{' '}
      and{' '}
      <a className='font-semibold text-md-pink-dark underline' href={`${process.env.REACT_APP_SHOPIFY_URL}/policies/privacy-policy`} target='_blank' rel='noreferrer'>
        Privacy Policy
      </a>{' '}
      and have the rights to the photos I upload.
    </Checkbox>
  );
  const primary = (
    <Button size='lg' fullWidth onClick={submit} disabled={!canContinue} busy={isLoading}>
      {isLoading ? 'Saving' : 'Next: Cover photo'}
    </Button>
  );
  // The cover as it prints, with the title as typed (the drawing comes after the cover photo step).
  const coverPreview = (
    <CoverPreview
      title={title}
      placeholder={
        <div className='flex h-full flex-col items-center justify-center gap-1.5 text-[#8A7F78]'>
          <ImageIcon className='h-7 w-7' aria-hidden='true' />
          <span className='text-[13px] text-md-muted'>Cover photo next</span>
        </div>
      }
    />
  );

  return (
    <WizardShell
      aside={
        <BookSidePanel
          cover={coverPreview}
          rows={showPrice ? [{ label: 'Cover + 20 pages', value: `$${BASE_BOOK_PRICE.toFixed(2)}` }] : []}
          primary={primary}
        />
      }
      bottomBar={<BottomBar primary={primary}>{terms}</BottomBar>}
    >
      <form
        className='flex flex-col gap-6'
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className='flex flex-col gap-1.5'>
          <h1 className='m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]'>Name your book</h1>
          <p className='m-0 text-[15px] leading-snug text-md-muted md:text-[17px]'>This prints on the front cover. You can change it later.</p>
        </div>

        <TextField
          id='book-title'
          name='bookName'
          label='Book title'
          value={title}
          onChange={(e) => {
            // Like the live app's cover title box: no third line on the cover (it would print smaller, on 3 lines).
            const next = e.target.value;
            if (next.length > title.length && !titleFitsCover(next)) {
              setTitleFull(true);
              return;
            }
            setTitleFull(false);
            handleChange(e);
          }}
          // No "N/40" counter: what fits depends on the letters, not the count (decided 2026-10-23).
          maxLength={40}
          autoComplete='off'
          helper={titleFull ? "That's as long as fits on the cover." : undefined}
          // TODO(design): the "can't print that" message isn't drawn (docs/design/GAPS.md).
          error={title.trim() ? titleProblem(title) || undefined : errors?.bookName?.message ? titleProblem(title) : undefined}
        />

        <div className='flex flex-col gap-2.5'>
          <div className='text-sm font-semibold text-md-dark'>Need an idea?</div>
          <div className='flex flex-wrap gap-2'>
            {ideas.map((idea) => (
              <Button key={idea} variant='secondary' size='sm' className='!border !font-medium' onClick={() => pickIdea(idea)}>
                {idea}
              </Button>
            ))}
          </div>
        </div>

        {/* Tablet only: the cover preview sits in the page; desktop has it in the side panel. */}
        <div className='hidden items-center gap-7 rounded-[20px] border border-md-pink-light-1 bg-white p-7 md:flex lg:hidden'>
          <div className='w-[180px] shrink-0 overflow-hidden rounded-[10px]'>{coverPreview}</div>
          <div className='flex flex-col gap-1.5 text-md-muted'>
            <div className='text-[13px] font-bold uppercase tracking-[0.06em] text-md-muted'>Your cover</div>
            <div className='text-lg font-semibold text-md-dark'>The title appears here as you type.</div>
            <div className='text-[15px]'>Add your cover photo next.</div>
          </div>
        </div>

        {/* Desktop keeps the terms in the page, as the mockup does; mobile and tablet have them in the bottom bar. */}
        <div className='hidden border-t border-md-pink-light-1 pt-5 lg:block'>{terms}</div>

        {error ? (
          <p role='alert' className='m-0 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-900'>
            {error?.message || 'Something went wrong. Please try again.'}
          </p>
        ) : null}
      </form>
    </WizardShell>
  );
};

export default BookCreation;
